/**
 * Pure helpers that turn a stored agentic trajectory
 * (`record.trajectory`: `[{ role, content, tool_call: { name, arguments } | null }]`)
 * into what the Data Editor renders: chat bubbles, tool-call cards, the
 * `name → summary` header and the TOOLS CALLED counts.
 */

/** Characters of the tool message kept after the arrow in a tool-call header. */
export const TOOL_SUMMARY_MAX_CHARS = 40;

/**
 * Spark `TagColored` variants in the order the Figma TOOLS CALLED column
 * assigns them to tools (accent-01 pink → accent-04 blue → accent-06 lime),
 * then the remaining palette so more than three tools stay distinguishable.
 */
export const TOOL_TAG_VARIANTS = [
    'pink',
    'blue',
    'lime',
    'purple',
    'cyan',
    'burgundy',
    'volcano',
    'peach',
    'yellow',
];

export const TRAJECTORY_ROLES = ['user', 'assistant', 'tool'];

/**
 * @param {unknown} value
 * @returns {string} text for a message content that may not be a plain string
 *   (parquet structs / JSON objects are pretty-printed, nullish becomes '').
 */
export function contentToText(value) {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return String(value);
    }
}

/**
 * Pretty-print tool-call arguments. Objects become indented JSON; a string
 * that parses as JSON is re-indented; anything else is shown verbatim.
 *
 * @param {unknown} args
 * @returns {string}
 */
export function formatArguments(args) {
    if (args === null || args === undefined) return '';
    if (typeof args === 'string') {
        const trimmed = args.trim();
        if (!trimmed) return '';
        try {
            return JSON.stringify(JSON.parse(trimmed), null, 2);
        } catch {
            return args;
        }
    }
    return contentToText(args);
}

/**
 * Tool responses are stored as the following `tool` message's content. Show
 * JSON payloads indented, everything else as-is.
 *
 * @param {unknown} content
 * @returns {string}
 */
export function formatToolResponse(content) {
    const text = contentToText(content);
    const trimmed = text.trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
            return JSON.stringify(JSON.parse(trimmed), null, 2);
        } catch {
            return text;
        }
    }
    return text;
}

/**
 * `<tool_name> → <first 40 chars of the tool message content>`; whitespace is
 * collapsed, longer responses end with an ellipsis, and a call without a
 * recorded response is summarised by its name only.
 *
 * @param {string} name
 * @param {unknown} responseContent
 * @param {number} [maxChars]
 * @returns {string}
 */
export function toolCallSummary(name, responseContent, maxChars = TOOL_SUMMARY_MAX_CHARS) {
    const head = toolResponseHead(responseContent, maxChars);
    return head ? `${name} → ${head}` : name;
}

/**
 * The part after the arrow: the tool message's first `maxChars` characters
 * (whitespace collapsed, ellipsis when cut), or '' without a response.
 *
 * @param {unknown} responseContent
 * @param {number} [maxChars]
 * @returns {string}
 */
export function toolResponseHead(responseContent, maxChars = TOOL_SUMMARY_MAX_CHARS) {
    const collapsed = contentToText(responseContent).replace(/\s+/g, ' ').trim();
    if (!collapsed) return '';
    return collapsed.length > maxChars ? `${collapsed.slice(0, maxChars).trimEnd()}…` : collapsed;
}

/**
 * @param {unknown} record dataset record (flattened `SingleDataRecord`)
 * @returns {Array<{ role: string, content: unknown, tool_call: { name: string, arguments?: unknown } | null }>}
 *   the stored trajectory, or `[]` when the record has none.
 */
export function trajectoryMessages(record) {
    const messages = record?.trajectory;
    if (!Array.isArray(messages)) return [];
    return messages
        .filter((message) => message && typeof message === 'object')
        .map((message) => ({
            role: TRAJECTORY_ROLES.includes(message.role) ? message.role : 'user',
            content: message.content,
            tool_call: message.tool_call && typeof message.tool_call === 'object' && message.tool_call.name
                ? { name: String(message.tool_call.name), arguments: message.tool_call.arguments }
                : null,
        }));
}

/** @param {unknown} record */
export function hasTrajectory(record) {
    return trajectoryMessages(record).length > 0;
}

/**
 * Group a trajectory into renderable items. An assistant turn that carries a
 * `tool_call` is paired with the next `tool` message (its response); its own
 * text, when present, is still shown as an assistant bubble before the card.
 * A `tool` message that no call requested stays a plain tool bubble.
 *
 * @param {ReturnType<typeof trajectoryMessages>} messages
 * @returns {Array<
 *   | { kind: 'message', key: string, role: string, content: string }
 *   | { kind: 'tool_call', key: string, name: string, arguments: string, response: string, summary: string }
 * >} `summary` is the text after the arrow (`''` without a tool response).
 */
export function groupTrajectory(messages) {
    const items = [];
    for (let index = 0; index < messages.length; index += 1) {
        const message = messages[index];
        const text = contentToText(message.content);
        if (message.role === 'assistant' && message.tool_call) {
            if (text.trim()) {
                items.push({ kind: 'message', key: `m-${index}`, role: 'assistant', content: text });
            }
            const next = messages[index + 1];
            const hasResponse = Boolean(next && next.role === 'tool');
            const responseContent = hasResponse ? next.content : null;
            items.push({
                kind: 'tool_call',
                key: `t-${index}`,
                name: message.tool_call.name,
                arguments: formatArguments(message.tool_call.arguments),
                response: formatToolResponse(responseContent),
                summary: toolResponseHead(responseContent),
            });
            if (hasResponse) index += 1;
            continue;
        }
        items.push({ kind: 'message', key: `m-${index}`, role: message.role, content: text });
    }
    return items;
}

/**
 * Tool names with how many times each was called, in first-call order
 * (drives the TOOLS CALLED pills).
 *
 * @param {ReturnType<typeof trajectoryMessages>} messages
 * @returns {Array<{ name: string, count: number }>}
 */
export function toolCallCounts(messages) {
    const counts = new Map();
    messages.forEach((message) => {
        if (message.role !== 'assistant' || !message.tool_call) return;
        const { name } = message.tool_call;
        counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts, ([name, count]) => ({ name, count }));
}

/**
 * Stable Spark tag variant for a tool, by its position in the record's
 * first-call order (matches the Figma pink / blue / lime sequence).
 *
 * @param {number} index
 * @returns {string}
 */
export function toolTagVariant(index) {
    return TOOL_TAG_VARIANTS[((index % TOOL_TAG_VARIANTS.length) + TOOL_TAG_VARIANTS.length) % TOOL_TAG_VARIANTS.length];
}

/**
 * Sorted, de-duplicated tool names for a Data Editor tab: the distinct names
 * reported by the server plus any committed filter selection (kept so an
 * applied filter stays visible even when its tool no longer occurs). Blank and
 * non-string entries are dropped. Plain code-unit sort, like the server.
 *
 * @param {unknown} toolNames distinct names from `dataset-records/tool-call-names`
 * @param {unknown} [selectedNames] committed `tools_called` filter
 * @returns {string[]}
 */
export function tabToolNames(toolNames, selectedNames = []) {
    const names = new Set();
    [toolNames, selectedNames].forEach((list) => {
        (Array.isArray(list) ? list : []).forEach((name) => {
            if (typeof name === 'string' && name.trim()) names.add(name);
        });
    });
    return Array.from(names).sort();
}

/**
 * Stable Spark tag variant per tool across a whole tab: each tool takes the
 * palette entry at its index in the tab's sorted distinct-tool list, so a tool
 * is the same color in every TOOLS CALLED row and in the column filter menu.
 *
 * @param {string[]} sortedToolNames output of {@link tabToolNames}
 * @returns {Map<string, string>} tool name → `TagColored` variant
 */
export function toolTagVariantsByName(sortedToolNames = []) {
    return new Map(
        (Array.isArray(sortedToolNames) ? sortedToolNames : []).map((name, index) => [name, toolTagVariant(index)]),
    );
}
